import React, { useEffect } from 'react';
import { useAppStore, useWallet } from '../stores/useAppStore';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { useToastStore } from '../stores/useToastStore';
import GlassCard from '../components/ui/GlassCard';
import { QRCodeSVG } from 'qrcode.react';
import { copyToClipboard } from '../utils/clipboardUtils';

const Deposit: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const wallet = useWallet();
  const fetchWallet = useAppStore((state) => state.fetchWallet);
  const showToast = useToastStore((state) => state.show);

  useEffect(() => {
    // 只有在没有钱包或者需要刷新当前钱包余额时才调用
    // 如果已经有选中的钱包，使用该钱包的网络和币种进行刷新
    if (wallet) {
      // 如果当前钱包已经是正确的网络，不需要再次 fetch (除非你想强制刷新余额)
      // 这里为了简单和确保最新数据，还是进行一次 fetch
      fetchWallet(wallet.currency, wallet.chain);
    } else {
      fetchWallet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWallet]);

  const handleCopyAddress = async () => {
    if (wallet?.address) {
      const success = await copyToClipboard(wallet.address);
      if (success) showToast('地址已复制', 'success');
    }
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />
        <PageHeader title="充币" onBack={() => setCurrentPage('wallet')} />

        <div className="px-4 py-4 pt-20 max-w-lg mx-auto">
          <GlassCard className="p-6 mb-6 overflow-hidden relative" hover={false}>
            {/* 装饰性背景 */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>

            <div className="relative z-10">
              <div className="flex justify-between items-center mb-8 px-1">
                <div className="text-white/80 text-[10px] font-black uppercase tracking-[0.2em]">
                  充值网络 (Network)
                </div>
                <div className="bg-emerald-500/10 text-emerald-400 px-4 py-1.5 rounded-xl border border-emerald-500/20 font-black text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-500/10">
                  {wallet?.chain || 'POLYGON'}
                </div>
              </div>

              <div className="flex flex-col items-center justify-center mb-10">
                <div className="relative group">
                  {/* 二维码容器 */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl blur opacity-20 group-hover:opacity-40 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative w-52 h-52 bg-white p-4 rounded-2xl shadow-2xl overflow-hidden flex items-center justify-center">
                    {wallet?.address ? (
                      <QRCodeSVG
                        value={wallet.address}
                        size={160}
                        level="H"
                        includeMargin={false}
                        imageSettings={{
                          src: '/favicon.ico', // 如果有 logo 可以加上
                          x: undefined,
                          y: undefined,
                          height: 24,
                          width: 24,
                          excavate: true,
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-slate-50 flex items-center justify-center text-slate-300 text-[10px] font-black uppercase tracking-widest text-center p-6 border-2 border-dashed border-slate-200 rounded-xl">
                        正在生成二维码...
                      </div>
                    )}
                  </div>

                  {/* 装饰角标 */}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs shadow-lg animate-bounce">
                    QR
                  </div>
                </div>
                <p className="mt-6 text-white/70 text-[10px] font-black uppercase tracking-[0.3em] text-center">
                  扫描上方二维码获取地址
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-center mb-2 px-1">
                    <label className="text-white/80 text-[9px] font-black uppercase tracking-widest">
                      我的专属充值地址
                    </label>
                    <span className="text-emerald-400 text-[9px] font-black uppercase tracking-widest animate-pulse">
                      USDT Only
                    </span>
                  </div>

                  <div className="flex items-center gap-3 bg-black/40 border border-white/5 rounded-2xl p-4 group/addr transition-all hover:bg-black/50 hover:border-white/10">
                    <code className="text-white/80 text-xs font-mono break-all flex-1 leading-relaxed tracking-wider">
                      {wallet?.address || '正在生成地址...'}
                    </code>
                    <button
                      onClick={handleCopyAddress}
                      className="w-10 h-10 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-xl text-white/60 transition-all active:scale-90"
                      title="复制地址"
                    >
                      <span className="text-lg">📋</span>
                    </button>
                  </div>
                </div>

                <GlassCard className="!bg-amber-500/5 border-amber-500/10 p-5 !rounded-2xl" hover={false}>
                  <div className="text-amber-400 font-black text-[10px] uppercase tracking-[0.2em] mb-3 flex items-center gap-2">
                    <span className="text-base">⚠️</span> 安全注意事项 (Rules)
                  </div>
                  <ul className="space-y-2.5">
                    {[
                      '仅支持 USDT 资产充值，转入其他资产无法找回',
                      `请务必确认选择 ${wallet?.chain || 'POLYGON'} 对应网络`,
                      '区块确认后约 5-10 分钟自动入账',
                      '最小充值金额为 10 USDT',
                    ].map((text, i) => (
                      <li key={i} className="flex gap-2 text-white/70 text-[10px] font-bold leading-relaxed">
                        <span className="text-amber-500/60">•</span>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </GlassCard>
              </div>
            </div>
          </GlassCard>
        </div>
      </div>
    </GradientBackground>
  );
};

export default Deposit;
